import { FlexBox } from "components/Common/FlexBox"
import { SearchResultWrapper, StyledSearchIngredients } from "./styled"
import SearchAutocomplete from "components/search/SearchAutocomplete"
import {
  useSearchFoods,
  useSuggestedSearchTexts,
} from "features/food/hooks/index"
import { Fragment, useState } from "react"
import SuggestedSearchTexts from "components/search/SuggestedSearchTexts"
import SearchFoodResults from "components/search/SearchFoodResults"
import { SearchFoodResultType } from "model/food/types"
import {
  isEdamaFood,
  isMyFood,
  isOpenFoodFactFood,
} from "model/food/utils/typeGuards/foodTypeGuards"
import EdamaFoodDetails from "components/foodDetails/EdamaFoodDetails"
import MyFoodDetails from "components/foodDetails/MyFoodDetails"
import OpenFoodFactFoodDetails from "components/foodDetails/OpenFoodFactFoodDetails"

enum IDisplayContent {
  SuggestedSearchText = "Suggested-search-text",
  SearchResults = "Search-results",
  FoodDetails = "Food-details",
}

const SearchFoods = ({
  onAddFood,
  setIsHideHeader,
}: {
  onAddFood: (food: SearchFoodResultType) => void
  setIsHideHeader: (isHide: boolean) => void
}) => {
  const {
    keyword,
    suggestedSearchTexts,
    handleKeywordChange,
    isNoSuggestedSearchTexts,
  } = useSuggestedSearchTexts()

  const {
    searchFoodsResults,
    handleSearchFoods,
    searchFoodsLoading,
    searchSession,
    noSearchedFoodFounded,
  } = useSearchFoods()

  const [displayContent, setDisplayContent] = useState<IDisplayContent>(
    searchFoodsResults.length !== 0
      ? IDisplayContent.SearchResults
      : IDisplayContent.SuggestedSearchText,
  )

  const [foodDetails, setFoodDetails] = useState<SearchFoodResultType | null>(
    null,
  )

  const onSelectFood = (food: SearchFoodResultType) => {
    setFoodDetails(food)
    setIsHideHeader(true)
    setDisplayContent(IDisplayContent.FoodDetails)
  }

  const onDeselectFood = () => {
    setFoodDetails(null)
    setIsHideHeader(false)
    setDisplayContent(IDisplayContent.SearchResults)
  }

  if (displayContent === IDisplayContent.FoodDetails && foodDetails) {
    return (
      <Fragment>
        {isEdamaFood(foodDetails) && (
          <EdamaFoodDetails
            onBack={onDeselectFood}
            food={foodDetails}
            onAdd={onAddFood}
            mode="add"
          />
        )}

        {isOpenFoodFactFood(foodDetails) && (
          <OpenFoodFactFoodDetails
            onBack={onDeselectFood}
            food={foodDetails}
            onAdd={onAddFood}
            mode="add"
          />
        )}

        {isMyFood(foodDetails) && (
          <MyFoodDetails
            onBack={onDeselectFood}
            food={foodDetails}
            onAdd={onAddFood}
            mode="add"
          />
        )}
      </Fragment>
    )
  }

  return (
    <StyledSearchIngredients>
      {displayContent !== IDisplayContent.FoodDetails && (
        <FlexBox
          style={{ padding: "0 var(--app-horizontal-padding)" }}
          $direction="column"
          $gap={16}
        >
          <SearchAutocomplete
            onFocus={() => {
              if (!keyword) {
                setDisplayContent(IDisplayContent.SuggestedSearchText)
              }
            }}
            placeholder="Search"
            value={keyword}
            onChange={(keyword) => {
              handleKeywordChange(keyword)
              if (displayContent !== IDisplayContent.SuggestedSearchText) {
                setDisplayContent(IDisplayContent.SuggestedSearchText)
              }
            }}
            onClose={() => {
              handleKeywordChange("")
            }}
            onBlur={() => {}}
            onSearch={() => {
              const forceReset = true
              handleSearchFoods({
                nextKeyword: keyword,
                forceReset,
              })
              setDisplayContent(IDisplayContent.SearchResults)
            }}
            visible={!!keyword}
          />
        </FlexBox>
      )}

      {displayContent === IDisplayContent.SuggestedSearchText && (
        <div style={{ padding: "0 var(--app-horizontal-padding)" }}>
          <SuggestedSearchTexts
            style={{ marginTop: 0 }}
            headerStyle={{
              padding: "8px 0px",
            }}
            isNoSuggestedSearchTexts={isNoSuggestedSearchTexts}
            suggestedTexts={suggestedSearchTexts}
            keyword={keyword}
            onSelect={(keyword) => {
              const forceReset = true
              handleSearchFoods({
                nextKeyword: keyword,
                forceReset,
              })
              setDisplayContent(IDisplayContent.SearchResults)
            }}
          />
        </div>
      )}
      {displayContent === IDisplayContent.SearchResults && (
        <SearchResultWrapper>
          <SearchFoodResults
            noSearchedFoodFounded={noSearchedFoodFounded}
            foods={searchFoodsResults}
            onLoad={() => handleSearchFoods({ nextKeyword: keyword })}
            loading={searchFoodsLoading}
            hasMore={!!searchSession}
            keyword={keyword}
            onSelectFood={(food) => onSelectFood(food)}
            onAddFood={(food) => onAddFood(food)}
            noCreateLinkIfNoFoodFounded={true}
          />
        </SearchResultWrapper>
      )}
    </StyledSearchIngredients>
  )
}

export default SearchFoods
